// src/MyTradersView.js
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, buildApiUrl } from "./api/client";
import AdvancedFiltersModal from "./components/AdvancedFiltersModal";
import ActionMenuButton from "./components/ActionMenuButton";
import { useAppDialog } from "./components/AppDialogProvider";
import AccountSettingSelect from "./components/AccountSettingSelect";
import CardGrid from "./components/CardGrid";
import CollectorTierBadge from "./components/CollectorTierBadge";
import getEditActionButtonStyle from "./components/editActionButtonStyle";
import { useAuth } from "./authContext";
import { useCurrentUser } from "./currentUserContext";
import CsvImportPanel from "./features/import/components/CsvImportPanel";
import { useCsvImport } from "./features/import/hooks/useCsvImport";
import CardsTable from "./features/traders/components/CardsTable";
import { DEFAULT_SORT as DEFAULT_TABLE_SORT } from "./features/traders/components/CardsTable";
import AddCardsModal from "./features/traders/components/AddCardsModal";
import EditCardModal from "./features/traders/components/EditCardModal";
import SelectionActions from "./features/traders/components/SelectionActions";
import TraderModal from "./features/traders/components/TraderModal";
import UploadTextModal from "./features/traders/components/UploadTextModal";
import { getListingPriceForCard, getResolvedListingOption } from "./features/traders/listingPricing";
import { renderConditionOption, renderEditionOption, renderLanguageOption } from "./components/dropdownOptionRenderers";
import { formatCollectorTierLimit, getCollectorTierDefinitionForRole } from "./utils/collectorTiers";
import usePaginationScroll from "./utils/usePaginationScroll";
import { getWantsBooleanLabel, getWantsConditionLabel, getWantsLanguageLabel } from "./wantsSettingsOptions";

const CARDS_PER_PAGE = 50;
const FILTER_META_DEFAULT = {
  editions: [],
  languages: [],
  conditions: [],
  priceRange: {
    min: 0,
    max: 0
  }
};
const LANGUAGE_LABELS = {
  en: "English",
  de: "German",
  fr: "French",
  es: "Spanish",
  it: "Italian",
  jp: "Japanese",
  cn: "Chinese",
  kr: "Korean",
  ru: "Russian",
  pt: "Portuguese"
};
const FOIL_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "nonfoil", label: "Non-foil only" },
  { value: "foil", label: "Foil only" }
];
const ALTERED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "notaltered", label: "Not altered" },
  { value: "altered", label: "Altered only" }
];
const SIGNED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "unsigned", label: "Not signed" },
  { value: "signed", label: "Signed only" }
];

function clampNumber(value, min, max, fallback) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(Math.max(numericValue, min), max);
}

function getLanguageLabel(languageCode) {
  if (!languageCode) {
    return "Unknown";
  }

  return LANGUAGE_LABELS[languageCode] || String(languageCode).toUpperCase();
}

function formatDefaultSettingsSummary(defaults) {
  return `${getWantsLanguageLabel(defaults.defaultLanguage)} · ${getWantsConditionLabel(defaults.defaultCondition)} · Foil: ${getWantsBooleanLabel(defaults.defaultFoil)} · Altered: ${getWantsBooleanLabel(defaults.defaultAltered)} · Signed: ${getWantsBooleanLabel(defaults.defaultSigned)}`;
}

function renderListingResultBadge(type) {
  const isSuccess = type === "success";

  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: isSuccess ? "#15803d" : "#dc2626",
        transform: "translateY(1px)",
        flex: "0 0 auto"
      }}
    >
      {isSuccess ? (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M11.667 3.5L5.25 9.917L2.333 7"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M3.5 3.5L10.5 10.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M10.5 3.5L3.5 10.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  );
}

function renderStatusAlertLine(type, text) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      {renderListingResultBadge(type)}
      <span>{text}</span>
    </div>
  );
}

function buildListingResultMessage(listedCount, skippedCount) {
  return (
    <div style={{ display: "grid", gap: "12px" }}>
      <div style={{ display: "grid", gap: skippedCount > 0 ? "6px" : 0 }}>
        {renderStatusAlertLine("success", `${listedCount} card${listedCount === 1 ? "" : "s"} listed.`)}
        {skippedCount > 0 ? (
          renderStatusAlertLine("error", `${skippedCount} card${skippedCount === 1 ? " was" : "s were"} not listed due to having no price.`)
        ) : null}
      </div>
      {listedCount > 0 ? (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: "12px",
            border: "1px solid #dbe4f0",
            background: "#f8fafc",
            fontSize: "12px",
            fontStyle: "italic",
            color: "#475569"
          }}
        >
          Listings using Trend or Foil Trend follow the daily reference price from Scryfall/Cardmarket, so their prices may change from day to day.
        </div>
      ) : null}
    </div>
  );
}

function buildDelistingResultMessage(delistedCount) {
  return renderStatusAlertLine("success", `${delistedCount} card${delistedCount === 1 ? "" : "s"} delisted.`);
}

function buildStatusErrorMessage(message) {
  return renderStatusAlertLine("error", message);
}

function getRangePercent(value, min, max) {
  if (max <= min) {
    return 0;
  }

  return ((value - min) / (max - min)) * 100;
}

function PriceRangeSlider({ min, max, step = 0.5, valueMin, valueMax, disabled, onChange }) {
  const lowerValue = Math.min(valueMin, valueMax);
  const upperValue = Math.max(valueMin, valueMax);
  const leftPercent = getRangePercent(lowerValue, min, max);
  const rightPercent = getRangePercent(upperValue, min, max);

  return (
    <div className={`price-range-slider${disabled ? " price-range-slider--disabled" : ""}`}>
      <div className="price-range-slider__track" aria-hidden="true" />
      <div
        className="price-range-slider__range"
        aria-hidden="true"
        style={{
          left: `${leftPercent}%`,
          width: `${Math.max(rightPercent - leftPercent, 0)}%`
        }}
      />
      <input
        className="price-range-slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={lowerValue}
        aria-label="Minimum price"
        disabled={disabled}
        onChange={(event) => {
          const nextValue = Math.min(Number(event.target.value), upperValue);
          onChange({ min: nextValue, max: upperValue });
        }}
      />
      <input
        className="price-range-slider__input price-range-slider__input--upper"
        type="range"
        min={min}
        max={max}
        step={step}
        value={upperValue}
        aria-label="Maximum price"
        disabled={disabled}
        onChange={(event) => {
          const nextValue = Math.max(Number(event.target.value), lowerValue);
          onChange({ min: lowerValue, max: nextValue });
        }}
      />
    </div>
  );
}

function getCardStackKey(card, isListed = false) {
  const listedState = card.visibilityGroup
    ? card.visibilityGroup === "listed"
    : isListed;

  return [
    card.card || card.name || "",
    card.edition || "",
    card.language || "",
    card.condition || "",
    String(Boolean(card.foil)),
    String(Boolean(card.altered)),
    String(Boolean(card.signed)),
    listedState ? String(card.sale_price ?? "") : ""
  ].join("||");
}

function getPendingTradeLockedQty(card) {
  return Math.max(0, Number(card?.pendingTradeLockedQty ?? card?.pending_trade_locked_qty) || 0);
}

function getAcceptedTradeLockedQty(card) {
  return Math.max(0, Number(card?.acceptedTradeLockedQty ?? card?.accepted_trade_locked_qty) || 0);
}

function getActiveTradeLockedQty(card) {
  return Math.max(
    getPendingTradeLockedQty(card) + getAcceptedTradeLockedQty(card),
    Number(card?.activeTradeLockedQty ?? card?.active_trade_locked_qty) || 0
  );
}

function getTradeLockLabel(card) {
  const pendingLockedQty = getPendingTradeLockedQty(card);
  const acceptedLockedQty = getAcceptedTradeLockedQty(card);

  if (pendingLockedQty > 0) {
    return pendingLockedQty === 1 ? "Pending" : `${pendingLockedQty} pending trade cards`;
  }

  if (acceptedLockedQty > 0) {
    return acceptedLockedQty === 1 ? "Accepted" : `${acceptedLockedQty} accepted trade cards`;
  }

  return null;
}

function withNormalizedTradeLocks(card) {
  const pendingTradeLockedQty = getPendingTradeLockedQty(card);
  const acceptedTradeLockedQty = getAcceptedTradeLockedQty(card);
  const activeTradeLockedQty = getActiveTradeLockedQty(card);

  return {
    ...card,
    pendingTradeLockedQty,
    acceptedTradeLockedQty,
    activeTradeLockedQty,
    isPendingTradeLocked: pendingTradeLockedQty > 0,
    isAcceptedTradeLocked: acceptedTradeLockedQty > 0,
    isTradeLocked: activeTradeLockedQty > 0,
    tradeLockLabel: getTradeLockLabel({ pendingTradeLockedQty, acceptedTradeLockedQty, activeTradeLockedQty })
  };
}

function buildTradeLockSegments(card, isListed = false) {
  const normalizedCard = withNormalizedTradeLocks(card);
  const totalQty = Math.max(1, Number(normalizedCard.qty) || 1);
  const pendingLockedQty = Math.min(totalQty, normalizedCard.pendingTradeLockedQty || 0);
  const acceptedLockedQty = Math.min(
    Math.max(totalQty - pendingLockedQty, 0),
    normalizedCard.acceptedTradeLockedQty || 0
  );
  const unlockedQty = Math.max(totalQty - pendingLockedQty - acceptedLockedQty, 0);
  const baseStackKey = getCardStackKey(normalizedCard, isListed);
  const rawSourceId = normalizedCard.sourceId || normalizedCard.id;
  const segments = [];

  if (pendingLockedQty > 0) {
    segments.push({
      ...normalizedCard,
      id: `${card.id}:pending-lock`,
      qty: pendingLockedQty,
      stackKey: `${baseStackKey}||trade-lock:pending`,
      sourceId: rawSourceId,
      actionIds: Array.from({ length: pendingLockedQty }, () => rawSourceId),
      pendingTradeLockedQty: pendingLockedQty,
      acceptedTradeLockedQty: 0,
      activeTradeLockedQty: pendingLockedQty,
      isPendingTradeLocked: true,
      isAcceptedTradeLocked: false,
      isTradeLocked: true,
      tradeLockLabel: pendingLockedQty === 1 ? "Pending" : `${pendingLockedQty} pending trade cards`
    });
  }

  if (acceptedLockedQty > 0) {
    segments.push({
      ...normalizedCard,
      id: `${card.id}:accepted-lock`,
      qty: acceptedLockedQty,
      stackKey: `${baseStackKey}||trade-lock:accepted`,
      sourceId: rawSourceId,
      actionIds: Array.from({ length: acceptedLockedQty }, () => rawSourceId),
      pendingTradeLockedQty: 0,
      acceptedTradeLockedQty: acceptedLockedQty,
      activeTradeLockedQty: acceptedLockedQty,
      isPendingTradeLocked: false,
      isAcceptedTradeLocked: true,
      isTradeLocked: true,
      tradeLockLabel: acceptedLockedQty === 1 ? "Accepted" : `${acceptedLockedQty} accepted trade cards`
    });
  }

  if (unlockedQty > 0 || segments.length === 0) {
    segments.push({
      ...normalizedCard,
      id: segments.length === 0 ? card.id : `${card.id}:available`,
      qty: unlockedQty > 0 ? unlockedQty : totalQty,
      stackKey: `${baseStackKey}||trade-lock:none`,
      sourceId: rawSourceId,
      actionIds: Array.from({ length: unlockedQty > 0 ? unlockedQty : totalQty }, () => rawSourceId),
      pendingTradeLockedQty: 0,
      acceptedTradeLockedQty: 0,
      activeTradeLockedQty: 0,
      isPendingTradeLocked: false,
      isAcceptedTradeLocked: false,
      isTradeLocked: false,
      tradeLockLabel: null
    });
  }

  return segments;
}

function buildVisibleCards(cards, isListed, expandedStackKeys, stackEnabled) {
  if (!stackEnabled) {
    return cards.flatMap((card) => buildTradeLockSegments(card, isListed));
  }

  const visibleCards = [];
  const groupIndexes = new Map();

  for (const card of cards) {
    const segments = buildTradeLockSegments(card, isListed);

    for (const segment of segments) {
      const stackKey = segment.stackKey;
      const qty = Math.max(1, Number(segment.qty) || 1);

      if (expandedStackKeys.includes(stackKey)) {
        for (let index = 0; index < qty; index += 1) {
        visibleCards.push({
          ...segment,
          id: `${segment.id}:split:${index}`,
          qty: 1,
          stackKey,
          sourceId: segment.sourceId || card.id,
          actionIds: [segment.sourceId || card.id],
          pendingTradeLockedQty: segment.isPendingTradeLocked ? 1 : 0,
          acceptedTradeLockedQty: segment.isAcceptedTradeLocked ? 1 : 0,
          activeTradeLockedQty: segment.isTradeLocked ? 1 : 0,
          isPendingTradeLocked: Boolean(segment.isPendingTradeLocked),
          isAcceptedTradeLocked: Boolean(segment.isAcceptedTradeLocked),
          isTradeLocked: Boolean(segment.isTradeLocked),
          tradeLockLabel: segment.isPendingTradeLocked ? "Pending" : (segment.isAcceptedTradeLocked ? "Accepted" : null)
        });
        }
        continue;
      }

      if (!groupIndexes.has(stackKey)) {
        groupIndexes.set(stackKey, visibleCards.length);
        visibleCards.push({
          ...segment,
          stackKey,
          actionIds: [...(segment.actionIds || [])]
        });
        continue;
      }

      const existingIndex = groupIndexes.get(stackKey);
      const existingCard = visibleCards[existingIndex];

      visibleCards[existingIndex] = {
        ...existingCard,
        qty: (existingCard.qty || 1) + qty,
        actionIds: [...(existingCard.actionIds || []), ...(segment.actionIds || [])],
        pendingTradeLockedQty: (existingCard.pendingTradeLockedQty || 0) + (segment.pendingTradeLockedQty || 0),
        acceptedTradeLockedQty: (existingCard.acceptedTradeLockedQty || 0) + (segment.acceptedTradeLockedQty || 0),
        activeTradeLockedQty: (existingCard.activeTradeLockedQty || 0) + (segment.activeTradeLockedQty || 0),
        isPendingTradeLocked: ((existingCard.pendingTradeLockedQty || 0) + (segment.pendingTradeLockedQty || 0)) > 0,
        isAcceptedTradeLocked: ((existingCard.acceptedTradeLockedQty || 0) + (segment.acceptedTradeLockedQty || 0)) > 0,
        isTradeLocked: ((existingCard.activeTradeLockedQty || 0) + (segment.activeTradeLockedQty || 0)) > 0,
        tradeLockLabel: getTradeLockLabel({
          pendingTradeLockedQty: (existingCard.pendingTradeLockedQty || 0) + (segment.pendingTradeLockedQty || 0),
          acceptedTradeLockedQty: (existingCard.acceptedTradeLockedQty || 0) + (segment.acceptedTradeLockedQty || 0)
        })
      };
    }
  }

  return visibleCards;
}

function countSelectedActionCards(cards, selectedIds) {
  return cards
    .filter(card => selectedIds.includes(card.id))
    .reduce((sum, card) => sum + ((card.actionIds && card.actionIds.length) || 1), 0);
}

function buildCollectionFilterPayload({
  searchTerm,
  editionFilter,
  languageFilter,
  conditionFilter,
  foilFilter,
  alteredFilter,
  signedFilter,
  priceMin,
  priceMax,
  effectivePriceMin,
  effectivePriceMax
}) {
  return {
    search: searchTerm ? String(searchTerm).trim() : "",
    edition: editionFilter || "",
    language: languageFilter || "",
    condition: conditionFilter || "",
    foil: foilFilter || "all",
    altered: alteredFilter || "all",
    signed: signedFilter || "all",
    minPrice: priceMin !== null ? effectivePriceMin : null,
    maxPrice: priceMax !== null ? effectivePriceMax : null
  };
}

function getEditRequestTargets(visibleCard, editValue, editId) {
  const allActionIds = visibleCard?.actionIds || [visibleCard?.sourceId || editId];
  const availableQty = Math.max(1, allActionIds.length || Number(visibleCard?.qty) || 1);
  const requestedQty = Math.min(availableQty, Math.max(1, Number(editValue?.qty) || availableQty));

  if (requestedQty < availableQty) {
    return allActionIds.slice(0, requestedQty).map((id) => ({ id, splitOne: true }));
  }

  if (visibleCard?.sourceId) {
    return [{ id: visibleCard.sourceId, splitOne: true }];
  }

  return [...new Set(allActionIds.map((id) => String(id)))].map((id) => ({ id, splitOne: false }));
}

function hasCardAttributeChanges(visibleCard, editValue) {
  if (!visibleCard || !editValue) {
    return false;
  }

  return (
    (editValue.edition ?? visibleCard.edition ?? "") !== (visibleCard.edition ?? "")
    || (editValue.language ?? visibleCard.language ?? "") !== (visibleCard.language ?? "")
    || (editValue.condition ?? visibleCard.condition ?? "") !== (visibleCard.condition ?? "")
    || Boolean(editValue.foil) !== Boolean(visibleCard.foil)
    || Boolean(editValue.altered) !== Boolean(visibleCard.altered)
    || Boolean(editValue.signed) !== Boolean(visibleCard.signed)
  );
}

function getVisibleCardQuantity(visibleCard, editId) {
  return Math.max(1, visibleCard?.actionIds?.length || Number(visibleCard?.qty) || 1);
}

function getVisibleCardActionCountMap(visibleCard, editId) {
  const ids = visibleCard?.actionIds?.length
    ? visibleCard.actionIds.map((id) => String(id))
    : [String(visibleCard?.sourceId || visibleCard?.id || editId)];

  return ids.reduce((counts, id) => {
    counts.set(id, (counts.get(id) || 0) + 1);
    return counts;
  }, new Map());
}

function countCards(cards) {
  return cards.reduce((sum, card) => sum + (card.qty || 1), 0);
}

function createPaginationState(page = 1) {
  return {
    page,
    limit: CARDS_PER_PAGE,
    totalItems: 0,
    totalPages: 1,
    totalCards: 0
  };
}

function createCollectionSummaryState() {
  return {
    totalTrendValue: 0,
    rarityCount: {
      common: 0,
      uncommon: 0,
      rare: 0,
      mythic: 0
    }
  };
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis-right", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis-left", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis-left", currentPage - 1, currentPage, currentPage + 1, "ellipsis-right", totalPages];
}

function PaginationControls({ currentPage, totalItems, totalPages, label, onPageChange, position = "top" }) {
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * CARDS_PER_PAGE + 1;
  const endItem = Math.min(totalItems, currentPage * CARDS_PER_PAGE);
  const pageNumbers = getVisiblePageNumbers(currentPage, totalPages);
  const isBottom = position === "bottom";

  const commitPageInput = () => {
    const nextPage = Number.parseInt(pageInput, 10);

    if (!Number.isFinite(nextPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const clampedPage = Math.min(Math.max(nextPage, 1), totalPages);
    setPageInput(String(clampedPage));

    if (clampedPage !== currentPage) {
      onPageChange(clampedPage);
    }
  };

  return (
    <div
      className="app-pagination"
      style={{
        margin: isBottom ? "18px 0 0" : "0 0 18px",
        paddingTop: isBottom ? "10px" : 0,
        borderTop: isBottom ? "1px solid #e2e8f0" : "none"
      }}
    >
      <span className="app-pagination__summary">
        Showing {startItem}-{endItem} of {totalItems} {label}
      </span>
      <div className="app-pagination__controls">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
          title="Previous page"
          disabled={currentPage === 1}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage === 1 ? "#f8fafc" : "#ffffff",
            color: currentPage === 1 ? "#94a3b8" : "#0f172a",
            cursor: currentPage === 1 ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{"<"}</span>
        </button>
        <div className="app-pagination__page-list">
          {pageNumbers.map((pageNumber) => {
            if (typeof pageNumber !== "number") {
              return (
                <span key={pageNumber} className="app-pagination__ellipsis">
                  ...
                </span>
              );
            }

            const isActive = pageNumber === currentPage;

            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => onPageChange(pageNumber)}
                aria-current={isActive ? "page" : undefined}
                className="app-pagination__button"
                style={{
                  border: isActive ? "1px solid #0f766e" : "1px solid #cbd5e1",
                  background: isActive ? "#0f766e" : "#ffffff",
                  color: isActive ? "#ffffff" : "#0f172a",
                  cursor: "pointer"
                }}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
          title="Next page"
          disabled={currentPage === totalPages}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage === totalPages ? "#f8fafc" : "#ffffff",
            color: currentPage === totalPages ? "#94a3b8" : "#0f172a",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{">"}</span>
        </button>
        <label className="app-pagination__jump">
          Page
          <div className="app-pagination__jump-controls">
            <div className="app-pagination__select-shell">
              <input
                type="number"
                min="1"
                max={String(totalPages)}
                step="1"
                inputMode="numeric"
                aria-label="Jump to page"
                className="app-pagination__page-input"
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitPageInput();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    setPageInput(String(currentPage));
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="app-pagination__button app-pagination__button--text app-pagination__jump-button"
              onClick={commitPageInput}
            >
              Go
            </button>
          </div>
        </label>
      </div>
    </div>
  );
}

export default function MyTradersView() {
  const { user } = useAuth();
  const { selectedUserId } = useCurrentUser();
  const { showAlert, showConfirm } = useAppDialog();
  const pendingScrollRestoreRef = useRef(null);
  const [uploadDefaults, setUploadDefaults] = useState({ defaultLanguage: "en", defaultCondition: "NM", defaultFoil: "false", defaultAltered: "false", defaultSigned: "false" });
  const editMode = false;
  const [viewMode, setViewMode] = useState("images");
  const [cards, setCards] = useState([]);
  const [pagination, setPagination] = useState(() => createPaginationState());
  const [collectionSummary, setCollectionSummary] = useState(() => createCollectionSummaryState());
  const [collectionCounts, setCollectionCounts] = useState({ unlisted: 0, listed: 0 });
  const [collectionLimits, setCollectionLimits] = useState(null);
  const [selectedCards, setSelectedCards] = useState([]);
  const [listingOptions, setListingOptions] = useState({});
  const [showListed, setShowListed] = useState(true);
  const [showUnlisted, setShowUnlisted] = useState(true);
  const [filterMeta, setFilterMeta] = useState(FILTER_META_DEFAULT);
  const [setMetadata, setSetMetadata] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editionFilter, setEditionFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [foilFilter, setFoilFilter] = useState("all");
  const [alteredFilter, setAlteredFilter] = useState("all");
  const [signedFilter, setSignedFilter] = useState("all");
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [error, setError] = useState(null);
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingTextEditIds, setSavingTextEditIds] = useState({});
  const [expandedStackKeys, setExpandedStackKeys] = useState([]);
  const [sortConfig, setSortConfig] = useState(DEFAULT_TABLE_SORT);
  const [page, setPage] = useState(1);
  const [isAddCardsModalOpen, setIsAddCardsModalOpen] = useState(false);
  const [isImportCsvModalOpen, setIsImportCsvModalOpen] = useState(false);
  const [isUploadTextModalOpen, setIsUploadTextModalOpen] = useState(false);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [activeEditCardId, setActiveEditCardId] = useState(null);
  const [hoveredEditActionId, setHoveredEditActionId] = useState(null);
  const priceBounds = filterMeta.priceRange || FILTER_META_DEFAULT.priceRange;
  const rawPriceMin = clampNumber(priceMin, priceBounds.min, priceBounds.max, priceBounds.min);
  const rawPriceMax = clampNumber(priceMax, priceBounds.min, priceBounds.max, priceBounds.max);
  const effectivePriceMin = Math.min(rawPriceMin, rawPriceMax);
  const effectivePriceMax = Math.max(rawPriceMin, rawPriceMax);
  const hasPriceFilter = priceMin !== null || priceMax !== null;

  const resetTransientViewState = () => {
    setExpandedStackKeys([]);
    setSelectedCards([]);
    setListingOptions({});
    setActiveEditCardId(null);
  };

  // LOAD DATA
  const loadCards = useCallback(async (nextPage = page) => {
    try {
      const params = new URLSearchParams({
        page: String(nextPage),
        limit: String(CARDS_PER_PAGE),
        includeUnlisted: String(showUnlisted),
        includeListed: String(showListed)
      });

      if (searchTerm) {
        params.set("search", searchTerm);
      }

      if (editionFilter) {
        params.set("edition", editionFilter);
      }

      if (languageFilter) {
        params.set("language", languageFilter);
      }

      if (conditionFilter) {
        params.set("condition", conditionFilter);
      }

      if (foilFilter !== "all") params.set("foil", foilFilter);
      if (alteredFilter !== "all") params.set("altered", alteredFilter);
      if (signedFilter !== "all") params.set("signed", signedFilter);

      if (priceMin !== null) {
        params.set("minPrice", String(effectivePriceMin));
      }

      if (priceMax !== null) {
        params.set("maxPrice", String(effectivePriceMax));
      }

      if (sortConfig.key) {
        params.set("sortBy", sortConfig.key);
        params.set("sortDirection", sortConfig.direction);
      }

      const response = await apiFetch(`/api/traders/${selectedUserId}/collection?${params.toString()}`, { userId: selectedUserId });
      const data = await response.json().catch(() => ({}));
      const nextCards = Array.isArray(data?.cards) ? data.cards : [];
      const nextPagination = data?.pagination || {
        ...createPaginationState(nextPage),
        totalItems: nextCards.length,
        totalPages: Math.max(1, Math.ceil(nextCards.length / CARDS_PER_PAGE)),
        totalCards: countCards(nextCards)
      };
      const nextCounts = data?.counts || { unlisted: 0, listed: 0 };

      setCards(nextCards.map((card) => {
        const visibilityGroup = card.visibility_group || card.visibilityGroup || (card.sale_price != null ? "listed" : "unlisted");
        const sourceId = card.id;

        return {
          ...card,
          id: `${visibilityGroup}:${String(sourceId)}`,
          sourceId,
          visibilityGroup,
          pendingTradeLockedQty: Math.max(0, Number(card.pending_trade_locked_qty ?? card.pendingTradeLockedQty) || 0),
          acceptedTradeLockedQty: Math.max(0, Number(card.accepted_trade_locked_qty ?? card.acceptedTradeLockedQty) || 0),
          activeTradeLockedQty: Math.max(0, Number(card.active_trade_locked_qty ?? card.activeTradeLockedQty) || 0)
        };
      }));
      setFilterMeta(data?.filterMeta || FILTER_META_DEFAULT);
      setPagination(nextPagination);
      setCollectionSummary({
        totalTrendValue: Number(data?.summary?.totalTrendValue) || 0,
        rarityCount: {
          common: Number(data?.summary?.rarityCount?.common) || 0,
          uncommon: Number(data?.summary?.rarityCount?.uncommon) || 0,
          rare: Number(data?.summary?.rarityCount?.rare) || 0,
          mythic: Number(data?.summary?.rarityCount?.mythic) || 0
        }
      });
      setCollectionCounts({
        unlisted: Number(nextCounts.unlisted) || 0,
        listed: Number(nextCounts.listed) || 0
      });
      setCollectionLimits(data?.collectionLimits || null);
      if (nextPagination.page !== nextPage) {
        setPage(nextPagination.page);
      }
      setError(null);
    } catch (err) {
      console.error("Cards load error:", err);
      setError("Backend is not running. Make sure PostgreSQL and server on port 3000 are started.");
      setCards([]);
      setFilterMeta(FILTER_META_DEFAULT);
      setPagination(createPaginationState(nextPage));
      setCollectionSummary(createCollectionSummaryState());
      setCollectionCounts({ unlisted: 0, listed: 0 });
      setCollectionLimits(null);
    }
  }, [alteredFilter, conditionFilter, editionFilter, effectivePriceMax, effectivePriceMin, foilFilter, languageFilter, page, priceMax, priceMin, searchTerm, selectedUserId, showListed, showUnlisted, signedFilter, sortConfig.direction, sortConfig.key]);
  useEffect(() => {
    loadCards(page);
  }, [loadCards, page]);

  const handleTableSortChange = useCallback((nextSortConfig) => {
    setPage(1);
    setSortConfig(nextSortConfig);
  }, []);

  useEffect(() => {
    const setCodes = [...new Set(filterMeta.editions.map((edition) => String(edition.code || "").trim().toLowerCase()).filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`/api/traders/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId: selectedUserId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (isCancelled) {
          return;
        }

        setSetMetadata((current) => ({
          ...current,
          ...(data.sets || {})
        }));
      })
      .catch((error) => {
        console.error("Set metadata error:", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [filterMeta.editions, selectedUserId, setMetadata]);

  useEffect(() => {
    let isDisposed = false;

    async function loadUploadDefaults() {
      try {
        const response = await apiFetch(`/api/account/traders-settings/${selectedUserId}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load import defaults");
        }

        if (!isDisposed) {
          setUploadDefaults({
            defaultLanguage: String(data?.settings?.defaultLanguage || "en").toLowerCase(),
            defaultCondition: String(data?.settings?.defaultCondition || "NM").toUpperCase(),
            defaultFoil: String(data?.settings?.defaultFoil || "false").toLowerCase(),
            defaultAltered: String(data?.settings?.defaultAltered || "false").toLowerCase(),
            defaultSigned: String(data?.settings?.defaultSigned || "false").toLowerCase()
          });
        }
      } catch (err) {
        if (!isDisposed) {
          console.error("Trader import defaults load error:", err);
          setUploadDefaults({ defaultLanguage: "en", defaultCondition: "NM", defaultFoil: "false", defaultAltered: "false", defaultSigned: "false" });
        }
      }
    }

    loadUploadDefaults();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextSearchTerm = searchInput.trim();

      if (nextSearchTerm === searchTerm) {
        return;
      }

      resetTransientViewState();
      setPage(1);
      setSearchTerm(nextSearchTerm);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput, searchTerm]);

  const refreshCards = () => {
    setExpandedStackKeys([]);
    return loadCards(page);
  };

  const clearPendingEdit = (editId) => {
    setPendingEdits((prev) => {
      if (!prev[editId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[editId];
      return next;
    });
  };

  const setTextEditSaving = (editId, isSaving) => {
    setSavingTextEditIds((prev) => {
      if (isSaving) {
        return {
          ...prev,
          [editId]: true
        };
      }

      if (!prev[editId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[editId];
      return next;
    });
  };

  const {
    file,
    setFile,
    fileInputRef,
    importStatus,
    handleUpload,
    resetImportState,
    totalImportRows,
    isImportActive,
    isImportPreparing,
    showDeterminateImportProgress,
    importProgressPercent,
    importStatusLabel,
  } = useCsvImport({
    uploadUrl: buildApiUrl("/api/traders/upload"),
    getStatusUrl: (importId) => buildApiUrl(`/api/traders/upload/${importId}/status`),
    getRequestOptions: () => ({}),
    onCompleted: () => {
      refreshCards();
    }
  });

  const handleCloseImportCsvModal = useCallback(() => {
    setIsImportCsvModalOpen(false);

    if (!isImportActive) {
      resetImportState();
    }
  }, [isImportActive, resetImportState]);

  useEffect(() => {
    if (importStatus?.stage === "completed") {
      handleCloseImportCsvModal();
    }
  }, [handleCloseImportCsvModal, importStatus?.stage]);

  const handleOpenEditModal = (card) => {
    if (card.visibilityGroup === "listed" && card.isTradeLocked) {
      void showAlert({
        title: "Card Locked In Trade",
        message: "Cards locked in a trade cannot be edited. Cancel the trade first.",
        tone: "danger"
      });
      return;
    }

    setActiveEditCardId(card.id);
  };

  const renderEditActionButton = ({ card }) => {
    const actionId = String(card.id);
    const isHighlighted = hoveredEditActionId === actionId;

    return (
      <button
        type="button"
        aria-label={`Edit ${card.card || "card"}`}
        title={card.visibilityGroup === "listed" && card.isTradeLocked ? "Card locked in trade" : "Edit card"}
        onMouseEnter={() => {
          setHoveredEditActionId(actionId);
        }}
        onMouseLeave={() => {
          setHoveredEditActionId((currentId) => (currentId === actionId ? null : currentId));
        }}
        onClick={(event) => {
          event.stopPropagation();
          handleOpenEditModal(card);
        }}
        style={getEditActionButtonStyle(isHighlighted)}
      >
      <svg width="13" height="13" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <path d="M12.9 2.6a1.4 1.4 0 0 1 2 2L7.1 12.4 4 13l.6-3.1 8.3-7.3Z" fill="currentColor" />
        <path d="M10.9 4.3l2.8 2.8" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" />
        <path d="M3.8 14.2h10.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
      </button>
    );
  };

  const getCardSelectionGroup = (cardId) => {
    if (stackedUnlistedCards.some((card) => card.id === cardId)) {
      return "unlisted";
    }

    if (stackedListedCards.some((card) => card.id === cardId)) {
      return "listed";
    }

    return null;
  };

  const getSelectedCardsGroup = (cardIds) => {
    if (cardIds.some((cardId) => stackedUnlistedCards.some((card) => card.id === cardId))) {
      return "unlisted";
    }

    if (cardIds.some((cardId) => stackedListedCards.some((card) => card.id === cardId))) {
      return "listed";
    }

    return null;
  };

  // SELECT / DESELECT CARDS
  const toggleSelect = (cardId) => {
    setSelectedCards((prev) => {
      if (prev.includes(cardId)) {
        return prev.filter((id) => id !== cardId);
      }

      const clickedGroup = getCardSelectionGroup(cardId);
      const currentGroup = getSelectedCardsGroup(prev);

      if (currentGroup && clickedGroup && currentGroup !== clickedGroup) {
        return prev;
      }

      return [...prev, cardId];
    });
  };

  // LIST SELECTED CARDS
  const handleListSelected = async () => {
    const selectedUnlistedCards = stackedUnlistedCards.filter(card => selectedCards.includes(card.id));

    if (selectedUnlistedCards.length === 0) {
      return;
    }

    try {
      const items = selectedUnlistedCards.flatMap((card) => {
        const option = getResolvedListingOption(card, listingOptions);
        const actionIds = card.actionIds || [card.sourceId || card.id];

        return actionIds.map((actionId) => ({
          id: actionId,
          mode: option.mode,
          customPrice: option.mode === "custom" ? option.customPrice : ""
        }));
      });

      const response = await apiFetch("/api/traders/list/bulk", {
        userId: selectedUserId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || "Could not list selected cards");
      }

      const data = await response.json().catch(() => ({}));
      const listedCount = Number(data.listedCount) || 0;
      const skippedCount = Number(data.skippedCount) || 0;

      await showAlert({
        title: "Cards Listed",
        message: buildListingResultMessage(listedCount, skippedCount)
      });
      setExpandedStackKeys([]);
      setSelectedCards([]);
      setListingOptions({});
      refreshCards();
    } catch (err) {
      console.error("List cards error:", err);
      await showAlert({
        title: "List Selected Cards",
        message: err.message || "Could not list selected cards",
        tone: "danger"
      });
    }
  };

  const handleListAllMatchingUnlisted = async () => {
    if (!showUnlisted || unlistedCount === 0) {
      return;
    }

    const shouldListAllMatchingUnlisted = await showConfirm({
      title: "List All Matching Unlisted Cards",
      message: "List all currently unlisted cards? Non-foil cards use Trend and foil cards use Foil Trend when available.",
      confirmLabel: "List all"
    });

    if (!shouldListAllMatchingUnlisted) {
      return;
    }

    try {
      const response = await apiFetch("/api/traders/list/bulk", {
        userId: selectedUserId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selection: {
            type: "filtered-unlisted",
            filters: buildCollectionFilterPayload({
              searchTerm,
              editionFilter,
              languageFilter,
              conditionFilter,
              foilFilter,
              alteredFilter,
              signedFilter,
              priceMin,
              priceMax,
              effectivePriceMin,
              effectivePriceMax
            })
          },
          pricing: {
            mode: "auto"
          }
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not list matching unlisted cards");
      }

      await showAlert({
        title: "Cards Listed",
        message: buildListingResultMessage(Number(data.listedCount) || 0, Number(data.skippedCount) || 0)
      });
      resetTransientViewState();
      refreshCards();
    } catch (err) {
      console.error("List all matching cards error:", err);
      await showAlert({
        title: "List All Matching Unlisted Cards",
        message: err.message || "Could not list matching unlisted cards",
        tone: "danger"
      });
    }
  };

  const handleDelistAllMatchingListed = async () => {
    if (!showListed || listedCount === 0) {
      return;
    }

    const shouldDelistAllMatchingListed = await showConfirm({
      title: "Delist Matching Cards",
      message: "Delist all currently matching listed cards?",
      confirmLabel: "Delist all",
      tone: "danger"
    });

    if (!shouldDelistAllMatchingListed) {
      return;
    }

    try {
      const response = await apiFetch("/api/traders/unlist/bulk", {
        userId: selectedUserId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selection: {
            type: "filtered-listed",
            filters: buildCollectionFilterPayload({
              searchTerm,
              editionFilter,
              languageFilter,
              conditionFilter,
              foilFilter,
              alteredFilter,
              signedFilter,
              priceMin,
              priceMax,
              effectivePriceMin,
              effectivePriceMax
            })
          }
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not delist matching listed cards");
      }

      await showAlert({
        title: "Cards Delisted",
        message: buildDelistingResultMessage(Number(data.delistedCount) || 0)
      });
      resetTransientViewState();
      refreshCards();
    } catch (err) {
      console.error("Delist all matching cards error:", err);
      await showAlert({
        title: "Delist Matching Cards",
        message: buildStatusErrorMessage(err.message || "Could not delist matching listed cards"),
        tone: "danger"
      });
    }
  };

  const handleUpdatePricesSelected = async () => {
    const selectedListedCards = stackedListedCards.filter((card) => selectedCards.includes(card.id));

    if (selectedListedCards.length === 0) {
      return;
    }

    if (selectedListedCards.some((card) => card.isTradeLocked)) {
      await showAlert({
        title: "Card Locked In Trade",
        message: "Cards locked in a trade cannot be repriced. Cancel the trade first.",
        tone: "danger"
      });
      return;
    }

    try {
      const items = selectedListedCards.flatMap((card) => {
        const option = getResolvedListingOption(card, listingOptions);
        const price = getListingPriceForCard(card, listingOptions);
        const requestTargets = card.sourceId
          ? [{ id: card.sourceId, splitOne: true }]
          : [...new Set((card.actionIds || [card.id]).map((id) => String(id)))].map((id) => ({ id, splitOne: false }));

        return requestTargets.map((target) => ({
          id: target.id,
          splitOne: target.splitOne,
          price,
          pricing: {
            mode: option.mode,
            customPrice: option.mode === "custom" ? option.customPrice : ""
          }
        }));
      });

      const response = await apiFetch("/api/traders/update-price/bulk", {
        userId: selectedUserId,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || "Could not update selected card prices");
      }

      setExpandedStackKeys([]);
      setSelectedCards([]);
      setListingOptions({});
      refreshCards();
    } catch (err) {
      console.error("Update prices error:", err);
      await showAlert({
        title: "Update Selected Prices",
        message: err.message || "Could not update selected card prices",
        tone: "danger"
      });
    }
  };

  const handleSplitSelected = () => {
    const selectedStackKeys = [...stackedUnlistedCards, ...stackedListedCards]
      .filter(card => selectedCards.includes(card.id) && (card.qty || 1) > 1)
      .map(card => card.stackKey);

    if (selectedStackKeys.length === 0) {
      return;
    }

    setExpandedStackKeys(prev => [...new Set([...prev, ...selectedStackKeys])]);
    setSelectedCards([]);
    setListingOptions({});
  };

  const handleDelistSelected = async () => {
    const selectedListedCards = stackedListedCards.filter(card => selectedCards.includes(card.id));

    if (selectedListedCards.length === 0) {
      return;
    }

    if (selectedListedCards.some((card) => card.isTradeLocked)) {
      await showAlert({
        title: "Card Locked In Trade",
        message: buildStatusErrorMessage("Cards locked in a trade cannot be delisted. Cancel the trade first."),
        tone: "danger"
      });
      return;
    }

    try {
      const items = selectedListedCards.flatMap((card) => {
        const actionIds = card.actionIds || [card.sourceId || card.id];

        return actionIds.map((actionId) => ({ id: actionId }));
      });

      const response = await apiFetch("/api/traders/unlist/bulk", {
        userId: selectedUserId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || "Could not delist selected cards");
      }

      setExpandedStackKeys([]);
      setSelectedCards([]);
      setListingOptions({});
      refreshCards();
    } catch (err) {
      console.error("Delist cards error:", err);
      await showAlert({
        title: "Delist Selected Cards",
        message: buildStatusErrorMessage(err.message || "Could not delist selected cards"),
        tone: "danger"
      });
    }
  };

  const handleRemoveSelected = async () => {
    const selectedUnlistedCards = stackedUnlistedCards.filter((card) => selectedCards.includes(card.id));

    if (selectedUnlistedCards.length === 0) {
      return;
    }

    const shouldRemoveSelected = await showConfirm({
      title: "Remove Cards",
      message: `Remove ${selectedUnlistedCount} card${selectedUnlistedCount === 1 ? "" : "s"} from tradebinder?`,
      confirmLabel: "Remove cards",
      tone: "danger"
    });

    if (!shouldRemoveSelected) {
      return;
    }

    try {
      const items = selectedUnlistedCards.flatMap((card) => {
        const requestTargets = card.sourceId
          ? [{ id: card.sourceId, splitOne: true }]
          : (card.actionIds || [card.id]).map((id) => ({ id, splitOne: true }));

        return requestTargets.map((target) => ({
          id: target.id,
          splitOne: target.splitOne
        }));
      });

      const response = await apiFetch("/api/traders/delete/bulk", {
        userId: selectedUserId,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || "Could not remove selected cards");
      }

      setExpandedStackKeys([]);
      setSelectedCards([]);
      setListingOptions({});
      refreshCards();
    } catch (err) {
      console.error("Remove cards error:", err);
      await showAlert({
        title: "Remove Cards",
        message: err.message || "Could not remove selected cards",
        tone: "danger"
      });
    }
  };

  const handleRemoveAllUnlisted = async () => {
    if (unlistedCount === 0) {
      return;
    }

    const shouldRemoveAllUnlisted = await showConfirm({
      title: "Clear Unlisted Cards",
      message: `Remove all ${unlistedCount} unlisted card${unlistedCount === 1 ? "" : "s"} from My Traders?`,
      confirmLabel: "Remove all",
      tone: "danger"
    });

    if (!shouldRemoveAllUnlisted) {
      return;
    }

    try {
      const response = await apiFetch("/api/traders/delete/unlisted/all", {
        userId: selectedUserId,
        method: "DELETE"
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not remove all unlisted cards");
      }

      await showAlert({
        title: "Unlisted Cards Removed",
        message: `${Number(data.removedCount) || 0} unlisted card${Number(data.removedCount) === 1 ? "" : "s"} removed.`
      });
      resetTransientViewState();
      refreshCards();
    } catch (err) {
      console.error("Remove all unlisted cards error:", err);
      await showAlert({
        title: "Clear Unlisted Cards",
        message: err.message || "Could not remove all unlisted cards",
        tone: "danger"
      });
    }
  };

  const stackedVisibleCards = buildVisibleCards(cards, false, expandedStackKeys, true);
  const stackedUnlistedCards = stackedVisibleCards.filter((card) => card.visibilityGroup === "unlisted");
  const stackedListedCards = stackedVisibleCards.filter((card) => card.visibilityGroup === "listed");
  const visibleCardsById = stackedVisibleCards.reduce((map, card) => {
    map[card.id] = card;
    return map;
  }, {});
  const activeEditCard = activeEditCardId ? visibleCardsById[activeEditCardId] || null : null;
  const updateVisibleCardQuantity = async (editId, nextQty) => {
    const visibleCard = visibleCardsById[editId];

    if (visibleCard?.visibilityGroup === "listed" && visibleCard?.isTradeLocked) {
      throw new Error("Cards locked in a trade cannot be edited. Cancel the trade first.");
    }

    const currentQty = getVisibleCardQuantity(visibleCard, editId);
    const targetQty = Math.max(1, Number(nextQty) || 1);

    if (targetQty === currentQty) {
      return;
    }

    if (targetQty > currentQty) {
      const anchorId = String(visibleCard?.sourceId || visibleCard?.actionIds?.[0] || visibleCard?.id || editId);
      const response = await apiFetch(`/api/traders/update-quantity/${anchorId}`, {
        userId: selectedUserId,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ delta: targetQty - currentQty })
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.error || "Could not update card quantity");
      }

      return;
    }

    let remainingToRemove = currentQty - targetQty;
    const actionCountMap = getVisibleCardActionCountMap(visibleCard, editId);

    for (const [id, count] of actionCountMap.entries()) {
      if (remainingToRemove <= 0) {
        break;
      }

      const delta = -Math.min(count, remainingToRemove);
      const response = await apiFetch(`/api/traders/update-quantity/${id}`, {
        userId: selectedUserId,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ delta })
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.error || "Could not update card quantity");
      }

      remainingToRemove -= Math.abs(delta);
    }
  };
  const saveCardEdit = async (editId, editValue) => {
    const visibleCard = visibleCardsById[editId];

    if (visibleCard?.visibilityGroup === "listed" && visibleCard?.isTradeLocked) {
      throw new Error("Cards locked in a trade cannot be edited. Cancel the trade first.");
    }

    const requestedQty = Math.max(1, Number(editValue?.qty) || 1);
    const currentQty = getVisibleCardQuantity(visibleCard, editId);

    if (!hasCardAttributeChanges(visibleCard, editValue) && requestedQty !== currentQty) {
      await updateVisibleCardQuantity(editId, requestedQty);
      return;
    }

    const requestTargets = getEditRequestTargets(visibleCard, editValue, editId);
    const { edition, language, condition, foil, altered, signed, scryfallId } = editValue;

    for (const target of requestTargets) {
      const res = await apiFetch(`/api/traders/update/${target.id}`, {
        userId: selectedUserId,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ edition, language, condition, foil, altered, signed, scryfallId, splitOne: target.splitOne })
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.error || "Update failed");
      }
    }
  };
  const autoSaveTextCardEdit = async (editId, editValue) => {
    setTextEditSaving(editId, true);

    try {
      await saveCardEdit(editId, editValue);
      clearPendingEdit(editId);
      await loadCards(page);
    } catch (err) {
      console.error("Auto-save card edit error:", err);
      throw err;
    } finally {
      setTextEditSaving(editId, false);
    }
  };
  const unlistedCount = collectionCounts.unlisted;
  const listedCount = collectionCounts.listed;
  const hasVisibleCards = stackedVisibleCards.length > 0;
  const activeFilterCount = [
    Boolean(searchTerm),
    Boolean(editionFilter),
    Boolean(languageFilter),
    Boolean(conditionFilter),
    foilFilter !== "all",
    alteredFilter !== "all",
    signedFilter !== "all",
    hasPriceFilter
  ].filter(Boolean).length;
  const activeAdvancedFilterCount = [
    Boolean(editionFilter),
    Boolean(languageFilter),
    Boolean(conditionFilter),
    foilFilter !== "all",
    alteredFilter !== "all",
    signedFilter !== "all"
  ].filter(Boolean).length;
  const resultsSummaryLabel = pagination.totalItems === 1 ? "matching stack" : "matching stacks";
  const hasSelectedStacks = stackedVisibleCards.some(
    card => selectedCards.includes(card.id) && (card.qty || 1) > 1
  );
  const hasSelectedUnlistedCards = stackedUnlistedCards.some(card => selectedCards.includes(card.id));
  const hasSelectedListedCards = stackedListedCards.some(card => selectedCards.includes(card.id));
  const hasSelectedLockedListedCards = stackedListedCards.some(card => selectedCards.includes(card.id) && card.isTradeLocked);
  const selectedUnlistedCount = countSelectedActionCards(stackedUnlistedCards, selectedCards);
  const selectedListedCount = countSelectedActionCards(stackedListedCards, selectedCards);
  const selectedLockedListedCount = stackedListedCards
    .filter((card) => selectedCards.includes(card.id))
    .reduce((sum, card) => sum + (card.activeTradeLockedQty || 0), 0);
  const activeSelectionGroup = getSelectedCardsGroup(selectedCards);
  const showTextView = viewMode === "text";
  const resultsSectionRef = usePaginationScroll(pagination.page);
  const handlePageChange = async (nextPage) => {
    if (nextPage === page) {
      return;
    }

    if (Object.keys(pendingEdits).length > 0) {
      const shouldDiscardChanges = await showConfirm({
        title: "Discard Unsaved Changes",
        message: "Changing page will discard unsaved edits. Continue?",
        confirmLabel: "Discard changes",
        tone: "danger"
      });

      if (!shouldDiscardChanges) {
        return;
      }

      setPendingEdits({});
    }

    setExpandedStackKeys([]);
    setSelectedCards([]);
    setListingOptions({});
    setActiveEditCardId(null);

    setPage(nextPage);
  };

  useLayoutEffect(() => {
    if (pendingScrollRestoreRef.current === null) {
      return undefined;
    }

    const targetScrollTop = pendingScrollRestoreRef.current;
    const scrollingElement = document.scrollingElement || document.documentElement || document.body;

    const restoreScrollPosition = () => {
      if (scrollingElement) {
        scrollingElement.scrollTop = targetScrollTop;
      }

      window.scrollTo({ top: targetScrollTop, left: 0, behavior: "auto" });
    };

    restoreScrollPosition();

    const animationFrameOne = window.requestAnimationFrame(() => {
      restoreScrollPosition();

      window.requestAnimationFrame(() => {
        restoreScrollPosition();
      });
    });

    const timeoutId = window.setTimeout(() => {
      restoreScrollPosition();
      pendingScrollRestoreRef.current = null;
    }, 120);

    return () => {
      window.cancelAnimationFrame(animationFrameOne);
      window.clearTimeout(timeoutId);
    };
  }, [viewMode]);

  const handleViewModeChange = (nextViewMode) => {
    if (nextViewMode === viewMode) {
      return;
    }

    const scrollingElement = document.scrollingElement || document.documentElement || document.body;
    const scrollPosition = scrollingElement ? scrollingElement.scrollTop : window.scrollY;

    pendingScrollRestoreRef.current = scrollPosition;
    setViewMode(nextViewMode);
  };

  const updatePriceRangeFilter = (nextMin, nextMax, options = {}) => {
    const boundedMin = clampNumber(nextMin, priceBounds.min, priceBounds.max, priceBounds.min);
    const boundedMax = clampNumber(nextMax, priceBounds.min, priceBounds.max, priceBounds.max);
    const resolvedMin = Math.min(boundedMin, boundedMax);
    const resolvedMax = Math.max(boundedMin, boundedMax);
    const { preserveExplicitBounds = false } = options;

    resetTransientViewState();
    setPage(1);
    setPriceMin(!preserveExplicitBounds && Math.abs(resolvedMin - priceBounds.min) < 0.001 ? null : resolvedMin);
    setPriceMax(!preserveExplicitBounds && Math.abs(resolvedMax - priceBounds.max) < 0.001 ? null : resolvedMax);
  };

  const handlePriceRangeChange = ({ min: nextMin, max: nextMax }) => {
    updatePriceRangeFilter(nextMin, nextMax, { preserveExplicitBounds: true });
  };

  const handlePriceInputChange = (bound, rawValue) => {
    if (rawValue === "") {
      if (bound === "min") {
        updatePriceRangeFilter(priceBounds.min, effectivePriceMax);
        return;
      }

      updatePriceRangeFilter(effectivePriceMin, priceBounds.max);
      return;
    }

    const parsedValue = Number(rawValue);

    if (!Number.isFinite(parsedValue)) {
      return;
    }

    if (bound === "min") {
      updatePriceRangeFilter(parsedValue, effectivePriceMax);
      return;
    }

    updatePriceRangeFilter(effectivePriceMin, parsedValue);
  };

  const applyFilterValue = (setter, value, options = {}) => {
    resetTransientViewState();

    if (options.resetPriceRange) {
      setPriceMin(null);
      setPriceMax(null);
    }

    setPage(1);
    setter(value);
  };

  const clearAdvancedFilters = () => {
    resetTransientViewState();
    setPage(1);
    setEditionFilter("");
    setLanguageFilter("");
    setConditionFilter("");
    setFoilFilter("all");
    setAlteredFilter("all");
    setSignedFilter("all");
  };

  const handleToggleShowUnlisted = () => {
    resetTransientViewState();
    setPriceMin(null);
    setPriceMax(null);
    setPage(1);
    setShowUnlisted((value) => !value);
  };

  const handleToggleShowListed = () => {
    resetTransientViewState();
    setPriceMin(null);
    setPriceMax(null);
    setPage(1);
    setShowListed((value) => !value);
  };

  const canAdjustPriceRange = priceBounds.max > priceBounds.min;
  const currentUserRole = String(user?.role || "collector").trim().toLowerCase();
  const currentTraderTier = getCollectorTierDefinitionForRole(collectionLimits?.tier ?? user?.collectorTier, currentUserRole);
  const traderUsage = Number(collectionLimits?.usage?.myTraders || 0);
  const traderLimit = currentUserRole === "admin"
    ? currentTraderTier.myTradersLimit
    : collectionLimits?.limits?.myTraders ?? currentTraderTier.myTradersLimit;
  const summaryTotalValue = Number(collectionSummary.totalTrendValue) || 0;
  const summaryTotalCards = Number(pagination.totalCards) || 0;
  const summaryRarityCount = collectionSummary.rarityCount || createCollectionSummaryState().rarityCount;
  const addCardsAndSummaryGridStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    alignItems: "stretch",
    marginBottom: "20px"
  };
  const topCardStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "14px 16px",
    border: "1px solid #dbe4f0",
    borderRadius: "16px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
    minWidth: 0,
    width: "min(400px, 100%)",
    boxSizing: "border-box"
  };
  const topSummaryCardStyle = {
    ...topCardStyle,
    minWidth: "min(500px, 100%)",
    width: "min(500px, 100%)",
    gap: "14px"
  };

  return (
    <div className="app-responsive-shell collection-page collection-page--traders">
      <div className="account-page-header">
        <h1 style={{ margin: "0 0 16px" }}>My Traders</h1>
        <p className="account-copy">
          Keep the cards you are ready to trade organized, priced, and visible for matching against other users.
        </p>
      </div>
      {error && (
        <div
          style={{
            background: "#ffebee",
            border: "2px solid #c62828",
            color: "#c62828",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px",
            fontWeight: "bold"
          }}
        >
          ⚠️ {error}
        </div>
      )}
      <div className="collection-top-grid" style={addCardsAndSummaryGridStyle}>
        <div className="collection-top-card" style={topCardStyle}>
          <div>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>
              Manage Cards
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginTop: "8px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#0f172a", fontWeight: 700 }}>
                <CollectorTierBadge tier={currentTraderTier.id} role={currentUserRole} size="md" />
                {currentTraderTier.label}
              </span>
              <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                {new Intl.NumberFormat().format(traderUsage)} / {formatCollectorTierLimit(traderLimit)} cards in My Traders
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: "12px", lineHeight: 1.5, color: "#64748b", maxWidth: "720px" }}>
            <Link
              to="/account/my-traders-settings"
              style={{ color: "#0f766e", fontWeight: 700, textDecoration: "none", width: "fit-content" }}
            >
              My Traders Settings:
            </Link>
            <span>{formatDefaultSettingsSummary(uploadDefaults)}</span>
          </div>
          <div className="collection-top-actions" style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
            <ActionMenuButton
              label="Add Cards"
              buttonVariant="primary"
              items={[
                {
                  label: "Add 1 Card",
                  onClick: () => setIsAddCardsModalOpen(true)
                },
                {
                  label: "Import CSV",
                  onClick: () => setIsImportCsvModalOpen(true)
                },
                {
                  label: "Upload Text",
                  onClick: () => setIsUploadTextModalOpen(true)
                }
              ]}
            />
            <ActionMenuButton
              label="Bulk actions"
              items={[
                {
                  label: `List all (${unlistedCount})`,
                  onClick: handleListAllMatchingUnlisted,
                  disabled: !showUnlisted || unlistedCount === 0
                },
                {
                  label: `Delist all (${listedCount})`,
                  onClick: handleDelistAllMatchingListed,
                  disabled: !showListed || listedCount === 0
                },
                {
                  label: `Remove all (${unlistedCount})`,
                  onClick: handleRemoveAllUnlisted,
                  disabled: unlistedCount === 0,
                  tone: "danger"
                }
              ]}
            />
          </div>
        </div>

        <div className="collection-top-card" style={topSummaryCardStyle}>
          <div>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>
              Summary
            </div>
            <div style={{ fontSize: "14px", color: "#334155", marginTop: "8px" }}>
              Visible My Traders totals for the current filters.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", alignItems: "baseline", color: "#334155", fontSize: "14px" }}>
              <span>
                Value: <strong style={{ color: "#15803d" }}>{summaryTotalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong>
                <span
                  title="Total value is calculated from the filtered cards using their trend prices from Cardmarket."
                  aria-label="Total value is calculated from the filtered cards using their trend prices from Cardmarket."
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "16px",
                    height: "16px",
                    marginLeft: "6px",
                    transform: "translateY(-1px)",
                    borderRadius: "999px",
                    background: "#facc15",
                    color: "#713f12",
                    fontSize: "11px",
                    fontWeight: 800,
                    lineHeight: 1,
                    cursor: "help"
                  }}
                >
                  !
                </span>
              </span>
              <span>
                Cards: <strong>{summaryTotalCards}</strong>
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", alignItems: "baseline", color: "#334155", fontSize: "14px" }}>
              <span>Commons: <strong>{summaryRarityCount.common}</strong></span>
              <span>Uncommons: <strong>{summaryRarityCount.uncommon}</strong></span>
              <span>Rares: <strong>{summaryRarityCount.rare}</strong></span>
              <span>Mythics: <strong>{summaryRarityCount.mythic}</strong></span>
            </div>
          </div>
          <div style={{ fontSize: "12px", color: hasVisibleCards ? "#475569" : "#94a3b8", lineHeight: 1.5 }}>
            {hasVisibleCards
              ? `${pagination.totalItems} ${resultsSummaryLabel} match the current view.`
              : "No visible cards match the current filters right now."}
          </div>
        </div>
      </div>

      <AddCardsModal
        isOpen={isAddCardsModalOpen}
        onClose={() => setIsAddCardsModalOpen(false)}
        onAdded={() => {
          refreshCards();
          setSelectedCards([]);
          setListingOptions({});
        }}
        userId={selectedUserId}
        defaultLanguage={uploadDefaults.defaultLanguage}
        defaultCondition={uploadDefaults.defaultCondition}
        defaultFoil={uploadDefaults.defaultFoil}
        defaultAltered={uploadDefaults.defaultAltered}
        defaultSigned={uploadDefaults.defaultSigned}
        showLanguageField
      />

      <TraderModal
        isOpen={isImportCsvModalOpen}
        onClose={handleCloseImportCsvModal}
        title="Import CSV"
        width="760px"
      >
        <CsvImportPanel
          title="Upload your cards"
          file={file}
          fileInputRef={fileInputRef}
          isImportActive={isImportActive}
          importStatus={importStatus}
          isImportPreparing={isImportPreparing}
          showDeterminateImportProgress={showDeterminateImportProgress}
          importProgressPercent={importProgressPercent}
          importStatusLabel={importStatusLabel}
          totalImportRows={totalImportRows}
          onFileChange={setFile}
          onUpload={handleUpload}
        />
      </TraderModal>

      <UploadTextModal
        isOpen={isUploadTextModalOpen}
        onClose={() => setIsUploadTextModalOpen(false)}
        userId={selectedUserId}
        submitUrl="/api/traders/upload-text"
        defaultLanguage={uploadDefaults.defaultLanguage}
        defaultCondition={uploadDefaults.defaultCondition}
        defaultFoil={uploadDefaults.defaultFoil}
        defaultAltered={uploadDefaults.defaultAltered}
        defaultSigned={uploadDefaults.defaultSigned}
        onUploaded={() => {
          refreshCards();
          setSelectedCards([]);
          setListingOptions({});
        }}
      />
      <AdvancedFiltersModal
        isOpen={isAdvancedFiltersOpen}
        onClose={() => setIsAdvancedFiltersOpen(false)}
        title="Trader filters"
        activeCount={activeAdvancedFilterCount}
        onClear={clearAdvancedFilters}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Edition
            <AccountSettingSelect
              title="Edition"
              value={editionFilter}
              options={[
                { value: "", label: "All editions" },
                ...filterMeta.editions.map((edition) => ({
                  value: edition.code,
                  label: `${setMetadata[String(edition.code || "").trim().toLowerCase()]?.name || edition.label}${edition.code ? ` (${String(edition.code).toUpperCase()})` : ""}`,
                  setCode: String(edition.code || "").trim().toLowerCase(),
                  setMetadata: setMetadata[String(edition.code || "").trim().toLowerCase()],
                  showSetSymbol: Boolean(edition.code)
                }))
              ]}
              onChange={(nextValue) => applyFilterValue(setEditionFilter, nextValue)}
              renderSelectedValue={(nextValue, option) => renderEditionOption(nextValue, option, "All editions")}
              renderOption={(nextValue, option) => renderEditionOption(nextValue, option)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Language
            <AccountSettingSelect
              title="Language"
              value={languageFilter}
              options={[
                { value: "", label: "All languages" },
                ...filterMeta.languages.map((language) => ({ value: language, label: getLanguageLabel(language) }))
              ]}
              onChange={(nextValue) => applyFilterValue(setLanguageFilter, nextValue)}
              renderSelectedValue={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
              renderOption={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Condition
            <AccountSettingSelect
              title="Condition"
              value={conditionFilter}
              options={[
                { value: "", label: "All conditions" },
                ...filterMeta.conditions.map((condition) => ({ value: condition, label: condition }))
              ]}
              onChange={(nextValue) => applyFilterValue(setConditionFilter, nextValue)}
              renderSelectedValue={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
              renderOption={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Foil
            <AccountSettingSelect
              title="Foil"
              value={foilFilter}
              options={FOIL_FILTER_OPTIONS}
              onChange={(nextValue) => applyFilterValue(setFoilFilter, nextValue)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Altered
            <AccountSettingSelect
              title="Altered"
              value={alteredFilter}
              options={ALTERED_FILTER_OPTIONS}
              onChange={(nextValue) => applyFilterValue(setAlteredFilter, nextValue)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Signed
            <AccountSettingSelect
              title="Signed"
              value={signedFilter}
              options={SIGNED_FILTER_OPTIONS}
              onChange={(nextValue) => applyFilterValue(setSignedFilter, nextValue)}
            />
          </label>
        </div>
        <div className="collection-price-panel" style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px", color: "#334155", fontWeight: 600, padding: 0, minWidth: 0, marginTop: "16px" }}>
          <div className="collection-price-header" style={{ display: "flex", gap: "12px", alignItems: "baseline", flexWrap: "wrap" }}>
            <span>Price range</span>
          </div>
          <div className="collection-price-row" style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
            <label className="collection-price-field" style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "#64748b", fontWeight: 600, flex: "0 1 84px", minWidth: 0 }}>
              Min
              <input
                type="number"
                min={priceBounds.min}
                max={effectivePriceMax}
                step="0.5"
                value={priceMin ?? ""}
                placeholder={String(priceBounds.min)}
                disabled={!canAdjustPriceRange}
                onChange={(event) => handlePriceInputChange("min", event.target.value)}
                className="collection-input"
                style={{
                  height: "40px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  padding: "0 8px",
                  background: "#ffffff",
                  color: "#0f172a"
                }}
              />
            </label>
            <div className="collection-price-slider" style={{ flex: "1 1 120px", width: "auto", minWidth: "110px" }}>
              <PriceRangeSlider
                min={priceBounds.min}
                max={priceBounds.max}
                step={0.5}
                valueMin={effectivePriceMin}
                valueMax={effectivePriceMax}
                disabled={!canAdjustPriceRange}
                onChange={handlePriceRangeChange}
              />
            </div>
            <label className="collection-price-field" style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "#64748b", fontWeight: 600, flex: "0 1 84px", minWidth: 0 }}>
              Max
              <input
                type="number"
                min={effectivePriceMin}
                max={priceBounds.max}
                step="0.5"
                value={priceMax ?? ""}
                placeholder={String(priceBounds.max)}
                disabled={!canAdjustPriceRange}
                onChange={(event) => handlePriceInputChange("max", event.target.value)}
                className="collection-input"
                style={{
                  height: "40px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  padding: "0 8px",
                  background: "#ffffff",
                  color: "#0f172a"
                }}
              />
            </label>
          </div>
        </div>
      </AdvancedFiltersModal>
      {/* MODE TOGGLE */}
      <div className="collection-mode-bar" style={{ display: 'none' }}>
        <span style={{ fontWeight: 600 }}>View:</span>
        <div
          role="tablist"
          aria-label="View mode"
          style={{
            position: 'relative',
            display: 'inline-grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            padding: '4px',
            minWidth: '118px',
            borderRadius: '999px',
            background: '#e2e8f0',
            boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.12)'
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '4px',
              bottom: '4px',
              left: viewMode === 'images' ? '4px' : 'calc(50% + 2px)',
              width: 'calc(50% - 6px)',
              borderRadius: '999px',
              background: '#ffffff',
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.14)',
              transition: 'left 0.2s ease'
            }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'images'}
            aria-label="View as images"
            title="View as images"
            onClick={() => handleViewModeChange('images')}
            style={{
              position: 'relative',
              zIndex: 1,
              border: 'none',
              background: 'transparent',
              padding: '10px 16px',
              borderRadius: '999px',
              color: viewMode === 'images' ? '#0f172a' : '#64748b',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'color 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="2" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'text'}
            aria-label="View as text"
            title="View as text"
            onClick={() => handleViewModeChange('text')}
            style={{
              position: 'relative',
              zIndex: 1,
              border: 'none',
              background: 'transparent',
              padding: '10px 16px',
              borderRadius: '999px',
              color: viewMode === 'text' ? '#0f172a' : '#64748b',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'color 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <circle cx="3.5" cy="4" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="9" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="14" r="1.25" fill="currentColor" />
              <rect x="6" y="3" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="8" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="13" width="9" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>
      <div className="collection-top-grid">
      <div
        className="collection-filter-panel collection-top-card"
        style={{
          marginBottom: "16px",
          width: "min(916px, 100%)",
          padding: "14px",
          borderRadius: "16px",
          border: "1px solid #dbe4f0",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          boxShadow: "0 8px 18px rgba(15, 23, 42, 0.05)"
        }}
      >
        <div className="collection-filter-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "14px" }}>
          <div>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>
              Filter cards
            </div>
            <div style={{ fontSize: "14px", color: "#334155", marginTop: "4px" }}>
              {pagination.totalItems} {resultsSummaryLabel} · {pagination.totalCards} cards total
            </div>
          </div>
        </div>
        <div className="app-responsive-filters collection-filters" style={{ display: "grid", gap: "14px" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
            <label className="collection-field collection-field--wide" style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600, flex: "1 1 320px", width: "min(420px, 100%)", minWidth: "280px" }}>
              Search
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Card name, edition or collector #"
                className="collection-input"
                style={{
                  height: "42px",
                  width: "100%",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  padding: "0 14px",
                  boxSizing: "border-box",
                  background: "#ffffff",
                  color: "#0f172a"
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => setIsAdvancedFiltersOpen(true)}
              className="collection-button"
              style={{
                borderRadius: "12px",
                border: activeAdvancedFilterCount > 0 ? "1px solid #0f766e" : "1px solid #cbd5e1",
                background: activeAdvancedFilterCount > 0 ? "#ccfbf1" : "#ffffff",
                color: activeAdvancedFilterCount > 0 ? "#115e59" : "#0f172a",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                height: "42px",
                flex: "0 0 auto"
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 3.5h12l-4.6 5.2v3.2l-2.8 1.6V8.7L2 3.5Z" fill="currentColor" />
              </svg>
              {activeAdvancedFilterCount > 0 ? `Filters (${activeAdvancedFilterCount})` : "Filters"}
            </button>
          </div>
          <div className="collection-filter-actions" style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flexWrap: "wrap" }}>
            <button
              type="button"
              aria-pressed={showUnlisted}
              onClick={handleToggleShowUnlisted}
              className="collection-toggle-button"
              style={{
                gap: "8px",
                fontSize: "12px",
                borderRadius: "999px",
                border: `1px solid ${showUnlisted ? "#0f766e" : "#cbd5e1"}`,
                background: showUnlisted ? "#ccfbf1" : "#ffffff",
                color: showUnlisted ? "#115e59" : "#334155",
                cursor: "pointer",
                boxShadow: showUnlisted ? "0 6px 14px rgba(20, 184, 166, 0.14)" : "none"
              }}
            >
              <span style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: showUnlisted ? "#0f766e" : "#cbd5e1",
                flex: "0 0 auto"
              }} />
              Unlisted ({unlistedCount})
            </button>
            <button
              type="button"
              aria-pressed={showListed}
              onClick={handleToggleShowListed}
              className="collection-toggle-button"
              style={{
                gap: "8px",
                fontSize: "12px",
                borderRadius: "999px",
                border: `1px solid ${showListed ? "#0f766e" : "#cbd5e1"}`,
                background: showListed ? "#ccfbf1" : "#ffffff",
                color: showListed ? "#115e59" : "#334155",
                cursor: "pointer",
                boxShadow: showListed ? "0 6px 14px rgba(20, 184, 166, 0.14)" : "none"
              }}
            >
              <span style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: showListed ? "#0f766e" : "#cbd5e1",
                flex: "0 0 auto"
              }} />
              Listed ({listedCount})
            </button>
            {(!showListed && !showUnlisted) && (
              <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 600 }}>
                No cards selected
              </span>
            )}
          </div>
        </div>
      </div>
      </div>
      <div className="browse-users-view-toolbar" style={{ justifyContent: 'flex-start', gap: '1.5rem', marginTop: -4, marginBottom: 16 }}>
        <div
          className="browse-users-view-switch"
          role="tablist"
          aria-label="View mode"
        >
          <div
            aria-hidden="true"
            className="browse-users-view-switch-indicator"
            style={{ left: viewMode === 'images' ? '4px' : 'calc(50% + 2px)' }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'images'}
            aria-label="View as images"
            title="View as images"
            onClick={() => handleViewModeChange('images')}
            className="browse-users-view-switch-button"
            style={{ color: viewMode === 'images' ? '#0f172a' : '#64748b' }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="2" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'text'}
            aria-label="View as text"
            title="View as text"
            onClick={() => handleViewModeChange('text')}
            className="browse-users-view-switch-button"
            style={{ color: viewMode === 'text' ? '#0f172a' : '#64748b' }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <circle cx="3.5" cy="4" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="9" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="14" r="1.25" fill="currentColor" />
              <rect x="6" y="3" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="8" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="13" width="9" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>
      {/* FILTERED CARDGRID */}
      {(() => {
        if (!hasVisibleCards) {
          return <p>{activeFilterCount > 0 ? "No cards match the current filters." : "No cards selected."}</p>;
        }
        return <div ref={resultsSectionRef}>
          <PaginationControls
            currentPage={pagination.page}
            totalItems={pagination.totalItems}
            totalPages={pagination.totalPages}
            label="cards"
            onPageChange={handlePageChange}
          />
          {showTextView ? (
            <div className="app-responsive-table">
              <CardsTable
                cards={stackedVisibleCards}
                selectedCards={selectedCards}
                toggleSelect={toggleSelect}
                showSelectionIndicatorColumn={true}
                badgeType={(card) => card.visibilityGroup === "listed" ? "LISTED" : "UNLISTED"}
                userId={selectedUserId}
                listingOptions={listingOptions}
                setListingOptions={setListingOptions}
                editMode={editMode}
                pendingEdits={pendingEdits}
                setPendingEdits={setPendingEdits}
                savingEditIds={savingTextEditIds}
                onAutoSaveEdit={autoSaveTextCardEdit}
                sortConfig={sortConfig}
                onSortChange={handleTableSortChange}
                renderRowAction={({ card }) => renderEditActionButton({ card })}
              />
            </div>
          ) : (
            <CardGrid
              cards={stackedVisibleCards}
              selectedCards={selectedCards}
              toggleSelect={toggleSelect}
              badgeType={(card) => card.visibilityGroup === "listed" ? "LISTED" : "UNLISTED"}
              showPricePanel={true}
              preferNormalImage={true}
              listingOptions={listingOptions}
              setListingOptions={setListingOptions}
              pendingEdits={pendingEdits}
              setPendingEdits={setPendingEdits}
              renderCardAction={({ card }) => renderEditActionButton({ card })}
            />
          )}
          <PaginationControls
            currentPage={pagination.page}
            totalItems={pagination.totalItems}
            totalPages={pagination.totalPages}
            label="cards"
            onPageChange={handlePageChange}
            position="bottom"
          />
          <EditCardModal
            isOpen={Boolean(activeEditCard)}
            card={activeEditCard}
            pendingEdit={activeEditCard ? pendingEdits[activeEditCard.id] || {} : {}}
            onClose={() => setActiveEditCardId(null)}
            userId={selectedUserId}
            onApply={async (nextEdit) => {
              if (!activeEditCard) {
                return;
              }

              await saveCardEdit(activeEditCard.id, nextEdit);
              setPendingEdits((prev) => {
                const next = { ...prev };
                delete next[activeEditCard.id];
                return next;
              });
              refreshCards();
              setActiveEditCardId(null);
            }}
          />
        </div>;
      })()}
      <SelectionActions
        selectedCards={selectedCards}
        activeSelectionGroup={activeSelectionGroup}
        hasSelectedUnlistedCards={hasSelectedUnlistedCards}
        hasSelectedStacks={hasSelectedStacks}
        hasSelectedListedCards={hasSelectedListedCards}
        hasSelectedLockedListedCards={hasSelectedLockedListedCards}
        selectedUnlistedCount={selectedUnlistedCount}
        selectedListedCount={selectedListedCount}
        selectedLockedListedCount={selectedLockedListedCount}
        onList={handleListSelected}
        onUpdatePrices={handleUpdatePricesSelected}
        onSplit={handleSplitSelected}
        onDelist={handleDelistSelected}
        onRemove={handleRemoveSelected}
      />
    </div>
  );
}
// ...existing code...
